<template>
  <v-container v-if="selectedCannGroup" fluid class="ma-0 pa-0 variety-attrs-container">
    <div class="central-panel d-flex flex-column">
      <v-row class="align-end flex-grow-0">
        <v-col class="pl-5 py-2"> {{ $t('varietyGroups.customerAttrs') }}: </v-col>
        <v-col class="pl-0 py-2">
          <span class="regular-text">
            {{ $t('varietyGroups.constantValuesAttrs') }}
          </span>
          {{ $t('varietyGroups.constantValues') }}
        </v-col>
      </v-row>
      <div class="panel-container full-height">
        <v-col class="pt-0 first-panel">
          <variety-drag-container
            data-id-e2e="varAttrUnusedPanel"
            :disabled="isEditingDisabled"
            :items="unusedAttributes"
          />
        </v-col>
        <v-col class="pt-0 second-panel">
          <variety-drag-container
            data-id-e2e="varAttrRemainConstantPanel"
            :disabled="isEditingDisabled"
            :show-footer="true"
            :items="remainConstantAttributes"
          />
          <v-divider />
          <v-col class="pl-2 py-2 sticky-title">
            <span class="regular-text">
              {{ $t('varietyGroups.diffValuesAttrs') }}
            </span>
            {{ $t('varietyGroups.diffValues') }}
          </v-col>
          <variety-drag-container
            data-id-e2e="varAttrCanDifferPanel"
            :disabled="isEditingDisabled"
            :show-footer="true"
            :items="canDifferAttributes"
          />
        </v-col>
      </div>
      <div data-id-e2e="varietyGroupSaveActions">
        <PageActions
          class="actions-container pa-3"
          :has-data-changes="hasDataChanges"
          :has-data-errors="false"
          :show-discard="true"
          :show-save-button="true"
          :save-disabled="isEditingDisabled"
          :is-discard-enabled="!isEditingDisabled"
          @save="saveOne"
          @discard="init"
        />
      </div>
    </div>
  </v-container>
</template>

<script>
import { usePermission } from '@/composables/use-permission';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { cloneDeep, differenceWith, isEqual, sortBy } from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  props: { selectedCannGroup: { required: true, type: null } },
  setup() {
    const { canEditVarietyPage } = usePermission();

    return { canEditVarietyPage };
  },
  data() {
    return {
      varietyGroups: null,
      initialVarietyGroups: null,
    };
  },
  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useSnackbarStore, ['showError']),
    unusedAttributes() {
      if (!this.selectedScenario?.customAttributes) return [];
      const attributes = differenceWith(
        this.selectedScenario.customAttributes,
        [...this.remainConstantAttributes, ...this.canDifferAttributes],
        (x, y) => x.id === y.id
      );
      return sortBy(attributes, item => item.name.toLowerCase());
    },
    remainConstantAttributes() {
      if (
        !this.varietyGroups ||
        !this.selectedCannGroup ||
        !this.varietyGroups[this.selectedCannGroup]
      )
        return [];
      return this.varietyGroups[this.selectedCannGroup].remainConstantAttributes;
    },
    canDifferAttributes() {
      if (
        !this.varietyGroups ||
        !this.selectedCannGroup ||
        !this.varietyGroups[this.selectedCannGroup]
      )
        return [];
      return this.varietyGroups[this.selectedCannGroup].canDifferAttributes;
    },
    hasDataChanges() {
      // AOV3-405 required for variety-groups unsaved data warning
      return !isEqual(this.varietyGroups, this.initialVarietyGroups);
    },
    isEditingDisabled() {
      return !this.canEditVarietyPage;
    },
  },

  watch: {
    varietyGroups: {
      handler() {
        this.$emit('attributes-changed');
      },
      deep: true,
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...mapActions(useScenariosStore, ['updateVarietyGroups', 'setSelectedScenario']),

    init() {
      this.varietyGroups = this.getInitialValues();
      this.initialVarietyGroups = cloneDeep(this.varietyGroups);
    },

    varietyGroup({ cannGroupId, remainConstantAttributes, canDifferAttributes }) {
      return {
        cannGroupId,
        remainConstantAttributes: this.formatOnSave(remainConstantAttributes),
        canDifferAttributes: this.formatOnSave(canDifferAttributes),
      };
    },

    formatOnSave(list) {
      return list.map(attribute => attribute.id || attribute);
    },

    async saveAll() {
      const groupsToSave = Object.keys(this.varietyGroups).map(key =>
        this.varietyGroup({
          cannGroupId: key,
          remainConstantAttributes: this.varietyGroups[key].remainConstantAttributes,
          canDifferAttributes: this.varietyGroups[key].canDifferAttributes,
        })
      );
      await this.saveVarietyGroups(groupsToSave);
      this.init();
    },

    async saveOne() {
      const initialVarietyGroups = cloneDeep(this.selectedScenario.varietyGroups);
      const groupToSave = this.varietyGroup({
        cannGroupId: this.selectedCannGroup,
        remainConstantAttributes:
          this.varietyGroups[this.selectedCannGroup].remainConstantAttributes,
        canDifferAttributes: this.varietyGroups[this.selectedCannGroup].canDifferAttributes,
      });
      const index = initialVarietyGroups.findIndex(
        ({ cannGroupId }) => cannGroupId === this.selectedCannGroup
      );
      if (index === -1) initialVarietyGroups.push(groupToSave);
      else initialVarietyGroups[index] = groupToSave;
      return this.saveVarietyGroups(initialVarietyGroups);
    },

    async saveVarietyGroups(varietyGroups) {
      const { data, errors } = await this.updateVarietyGroups({
        id: this.selectedScenario._id,
        varietyGroups,
      });
      if (errors) {
        return this.showError(this.$t('errors.generalErrorMessage'));
      }
      if (data) {
        this.initialVarietyGroups = cloneDeep(this.varietyGroups);
        this.$emit('settings-saved');
        return this.setSelectedScenario(data);
      }
    },

    getInitialValues() {
      const varietyGroupsObject = {};
      const { varietyGroups, customAttributes, cannGroups } = this.selectedScenario;
      const scenarioCannGroups = this.getLeafNodes(cannGroups);
      scenarioCannGroups.forEach(({ key }) => {
        const found = varietyGroups.find(({ cannGroupId }) => cannGroupId === key);
        let remainConstantAttributes = [];
        let canDifferAttributes = [];
        if (found) {
          remainConstantAttributes = found.remainConstantAttributes.map(id => ({
            id,
            name: customAttributes.find(attribute => attribute.id === id).name,
          }));
          canDifferAttributes = found.canDifferAttributes.map(id => ({
            id,
            name: customAttributes.find(attribute => attribute.id === id).name,
          }));
        }
        varietyGroupsObject[key] = {
          remainConstantAttributes,
          canDifferAttributes,
        };
      });
      return varietyGroupsObject;
    },

    getLeafNodes(nodes = [], result = []) {
      for (let i = 0; i < nodes.length; i += 1) {
        if (!nodes[i].children.length) {
          result.push(nodes[i]);
        } else {
          result = this.getLeafNodes(nodes[i].children, result);
        }
      }
      return result;
    },

    /**
     * Move one or more attributes into a target box for the currently selected cann group.
     * Called by the AI agent action handler in VarietyGroups via $refs.varietyAttributes.
     *
     * @param {string[]} attributeNames - Display names of attributes to move
     * @param {'remain-constant'|'differ'|'unselected'} target - Destination box
     */
    moveAttributes(attributeNames, target) {
      if (!this.selectedCannGroup || !this.varietyGroups?.[this.selectedCannGroup]) return;

      const allAttributes = [...this.selectedScenario.customAttributes];

      const group = this.varietyGroups[this.selectedCannGroup];

      attributeNames.forEach(name => {
        const trimmed = name.trim();
        const attribute = allAttributes.find(a => a.name.toLowerCase() === trimmed.toLowerCase());
        if (!attribute) return;

        // Remove from both lists in-place (splice preserves Vue 3 reactivity)
        const rcIdx = group.remainConstantAttributes.findIndex(a => a.id === attribute.id);
        if (rcIdx !== -1) group.remainConstantAttributes.splice(rcIdx, 1);

        const cdIdx = group.canDifferAttributes.findIndex(a => a.id === attribute.id);
        if (cdIdx !== -1) group.canDifferAttributes.splice(cdIdx, 1);

        // Add to target list
        const entry = { id: attribute.id, name: attribute.name };
        if (target === 'remain-constant') {
          group.remainConstantAttributes.push(entry);
        } else if (target === 'differ') {
          group.canDifferAttributes.push(entry);
        }
        // 'unselected' means removed from both — already done above
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.variety-attrs-container {
  height: 100%;
}

.central-panel {
  height: 100%;
}

.panel-container {
  display: flex;
  flex-grow: 1;
  overflow: auto;

  &.full-height {
    height: $tab-panel-height;
  }
}

.first-panel {
  height: 100%;
  overflow: auto;
  border-right: 1px solid $assortment-border-colour;
}

.second-panel {
  overflow: auto;
}

.actions-container {
  border-top: 1px solid $assortment-border-colour;
}

.sticky-title {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: $assortment-header-footer-colour;
}

.regular-text {
  font-weight: 200;
  font-size: 12px;
}
</style>
